import React, { ReactNode, useId } from 'react';
import { classNames } from '@/utils/classNames';

interface FormFieldProps {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
  labelClassName?: string;
  helpTextClassName?: string;
  errorClassName?: string;
}

/**
 * アクセシブルなフォームフィールドラッパー
 * ラベル、説明、エラーメッセージを適切に関連付ける
 */
const FormField: React.FC<FormFieldProps> = ({
  label,
  description,
  error,
  required = false,
  children,
  className,
  labelClassName,
  helpTextClassName,
  errorClassName,
}) => {
  const fieldId = useId();
  const descriptionId = description ? `${fieldId}-description` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;

  // 子要素にIDとaria属性を追加
  const enhancedChildren = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      const ariaDescribedBy = [descriptionId, errorId].filter(Boolean).join(' ');

      return React.cloneElement(child as React.ReactElement<any>, {
        id: fieldId,
        'aria-describedby': ariaDescribedBy || undefined,
        'aria-invalid': error ? 'true' : undefined,
        'aria-required': required ? 'true' : undefined,
      });
    }
    return child;
  });

  return (
    <div className={classNames('space-y-1', className)}>
      {label && (
        <label
          htmlFor={fieldId}
          className={classNames(
            'block text-sm font-medium',
            error ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300',
            labelClassName
          )}
        >
          {label}
          {required && (
            <span className='ml-1 text-red-500' aria-label='必須'>
              *
            </span>
          )}
        </label>
      )}

      {description && (
        <p
          id={descriptionId}
          className={classNames('text-sm text-gray-500 dark:text-gray-400', helpTextClassName)}
        >
          {description}
        </p>
      )}

      {enhancedChildren}

      {error && (
        <p
          id={errorId}
          role='alert'
          className={classNames('text-sm text-red-600 dark:text-red-400', errorClassName)}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default FormField;
